import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';

const {
  FiCheck, FiTarget, FiTrendingUp, FiShield, FiFileText, FiPieChart,
  FiClock, FiArrowRight, FiAward, FiUsers
} = FiIcons;

const benefits = [
  {
    icon: FiClock,
    title: 'Know where you stand in under 2 minutes',
    text: "No spreadsheets, no guesswork. Enter a goal — your child's education, retirement, a house — and see the exact monthly SIP it needs, instantly."
  },
  {
    icon: FiTarget,
    title: 'Every goal planned on its own, correctly',
    text: 'Education, retirement, a home, a wedding — each is calculated independently with the right inflation and time horizon, so funding one goal never quietly starves another.'
  },
  {
    icon: FiTrendingUp,
    title: "See inflation's real bite before it surprises you",
    text: "Most parents underestimate future education costs by 3-4x. We project it accurately, years in advance, while there's still time to act."
  },
  {
    icon: FiShield,
    title: 'Stress-test your plan before life does',
    text: "Run a What-If scenario — costs 40% higher than expected — and know today whether your plan survives it, not after it's happened."
  },
  {
    icon: FiPieChart,
    title: 'One number for your entire financial life',
    text: 'A single, honest monthly SIP figure across every goal and retirement combined — so you know exactly what "on track" costs you each month.'
  },
  {
    icon: FiFileText,
    title: 'A plan you can act on, not just look at',
    text: 'Export a complete financial plan as a PDF — to keep for yourself, share with family, or hand to an advisor.'
  }
];

const included = [
  'Unlimited goal planning (education, retirement, house, wedding, and more)',
  'Child education planner with 30+ real Indian & international cost benchmarks',
  'Retirement corpus & drawdown planning with editable return assumptions',
  'What-If stress testing on every goal',
  'Full financial dashboard & net worth tracking',
  'Rent vs Buy, Home Loan, SWP, Insurance vs MF, Rental Yield calculators',
  'Downloadable PDF financial plan',
  'Data saved securely to your account'
];

function Pricing() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (isAuthenticated) {
      // Already a registered account — send them to the right place for
      // their current status rather than the app directly.
      navigate(user?.subscriptionStatus === 'active' ? '/details' : '/pending-approval');
    } else {
      navigate('/login', { state: { mode: 'register', from: { pathname: '/details' } } });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-12 md:py-20 max-w-5xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12 md:mb-16"
        >
          <div className="inline-flex items-center space-x-2 px-4 py-1.5 bg-[#19b48d]/10 rounded-full text-[#19b48d] text-sm font-bold mb-6">
            <SafeIcon icon={FiAward} className="text-sm" />
            <span>SEBI-Registered Research Analyst · INH000010858</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 tracking-tight mb-4">
            Financial freedom, made calculable.
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Most people never plan because it feels overwhelming. iNVESTT turns "will I be okay?"
            into a clear number for every goal in your life — in minutes, not months.
          </p>
        </motion.div>

        {/* Pricing Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden max-w-lg mx-auto mb-16 md:mb-20"
        >
          <div className="bg-gradient-to-br from-[#19b48d] to-[#16a085] p-8 text-center text-white">
            <p className="text-sm font-bold uppercase tracking-widest text-white/80 mb-2">Full Access</p>
            <div className="flex items-end justify-center space-x-1 mb-2">
              <span className="text-2xl font-bold mt-2">₹</span>
              <span className="text-5xl md:text-6xl font-black">5,999</span>
              <span className="text-lg font-medium text-white/80 mb-1">/year</span>
            </div>
            <p className="text-white/80 text-sm">That's about ₹16/day — less than a cup of tea.</p>
          </div>

          <div className="p-6 md:p-8">
            <button
              onClick={handleGetStarted}
              className="w-full h-14 bg-[#19b48d] text-white font-bold rounded-xl shadow-lg shadow-[#19b48d]/20 hover:bg-[#16a085] transition-all flex items-center justify-center space-x-2 mb-6"
            >
              <span>Get Started</span>
              <SafeIcon icon={FiArrowRight} />
            </button>

            <div className="space-y-3">
              {included.map((item, idx) => (
                <div key={idx} className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded-full bg-[#19b48d]/10 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <SafeIcon icon={FiCheck} className="text-[#19b48d] text-xs" />
                  </div>
                  <span className="text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Benefits Grid */}
        <div className="mb-16 md:mb-20">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-10">
            What this actually solves for you
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * idx }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6"
              >
                <div className="w-11 h-11 rounded-xl bg-[#19b48d]/10 flex items-center justify-center mb-4">
                  <SafeIcon icon={benefit.icon} className="text-[#19b48d] text-lg" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{benefit.text}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Trust strip */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10 text-center sm:text-left mb-12">
          <div className="flex items-center space-x-3">
            <SafeIcon icon={FiUsers} className="text-[#19b48d] text-2xl" />
            <div>
              <p className="font-bold text-gray-900">Built by SEBI-registered analysts</p>
              <p className="text-sm text-gray-500">Not generic templates — real planning methodology</p>
            </div>
          </div>
          <div className="hidden sm:block w-px h-10 bg-gray-200" />
          <div className="flex items-center space-x-3">
            <SafeIcon icon={FiShield} className="text-[#19b48d] text-2xl" />
            <div>
              <p className="font-bold text-gray-900">Your data stays yours</p>
              <p className="text-sm text-gray-500">Securely stored to your account, never sold</p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <button
            onClick={handleGetStarted}
            className="inline-flex items-center space-x-2 px-8 py-3.5 bg-[#19b48d] text-white font-bold rounded-xl shadow-lg shadow-[#19b48d]/20 hover:bg-[#16a085] transition-all"
          >
            <span>Start Planning Your Goals</span>
            <SafeIcon icon={FiArrowRight} />
          </button>
          <p className="text-xs text-gray-500 mt-4">
            Already a subscriber? <Link to="/login" className="font-bold text-[#19b48d] hover:text-[#16a085]">Log in</Link>
          </p>
        </div>

        <p className="text-xs text-gray-400 text-center mt-16 max-w-2xl mx-auto">
          Mutual fund investments are subject to market risks. Please read all scheme related documents
          carefully before investing. Past performance is not indicative of future returns. iNVESTT is a
          planning tool and does not constitute investment advice.
        </p>
      </div>
    </div>
  );
}

export default Pricing;